import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from './AuthContext';

const WishlistContext = createContext(null);

const PREFIX = 'oh:wishlist:v2:';
const guestKey = () => PREFIX + 'guest';
const userKey  = (id) => PREFIX + 'u' + id;
const LEGACY_KEY = 'oh:wishlist:v1';

function read(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) { return []; }
}
function write(key, items) {
  try { localStorage.setItem(key, JSON.stringify(items)); } catch (_) {}
}

function migrateLegacy() {
  try {
    const legacy = localStorage.getItem(LEGACY_KEY);
    if (!legacy) return;
    const existing = read(guestKey());
    const merged = unionById(existing, JSON.parse(legacy) || []);
    write(guestKey(), merged);
    localStorage.removeItem(LEGACY_KEY);
  } catch (_) {}
}

// Wishlist merge: union by id (no quantity), incoming wins for metadata.
function unionById(base, incoming) {
  const out = [...base];
  for (const inc of incoming || []) {
    if (!inc || inc.id == null) continue;
    const idx = out.findIndex((it) => String(it.id) === String(inc.id));
    if (idx >= 0) out[idx] = { ...out[idx], ...inc };
    else out.push(inc);
  }
  return out;
}

export function WishlistProvider({ children }) {
  const { user } = useAuth() || {};
  const currentKey = useMemo(
    () => (user?.id ? userKey(user.id) : guestKey()),
    [user?.id]
  );
  const prevKeyRef = useRef(currentKey);

  const [items, setItems] = useState(() => {
    migrateLegacy();
    return read(currentKey);
  });

  useEffect(() => {
    const prev = prevKeyRef.current;
    if (prev === currentKey) return;
    const prevIsGuest = prev === guestKey();
    const nextIsUser  = currentKey !== guestKey();
    if (prevIsGuest && nextIsUser) {
      const merged = unionById(read(currentKey), read(guestKey()));
      write(currentKey, merged);
      write(guestKey(), []);
      setItems(merged);
    } else {
      setItems(read(currentKey));
    }
    prevKeyRef.current = currentKey;
  }, [currentKey]);

  useEffect(() => {
    write(currentKey, items);
  }, [items, currentKey]);

  useEffect(() => {
    function onStorage(e) {
      if (e.key === currentKey) setItems(read(currentKey));
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [currentKey]);

  const has = (id) => items.some((it) => String(it.id) === String(id));
  const toggle = (product) => {
    if (!product?.id) return;
    setItems((cur) => {
      if (cur.some((it) => String(it.id) === String(product.id))) {
        return cur.filter((it) => String(it.id) !== String(product.id));
      }
      return [...cur, {
        id: product.id,
        name: product.name,
        brand: product.brand || '',
        price: typeof product.price === 'number' ? product.price : 0,
        image: product.image || product.image_url || '',
      }];
    });
  };
  const remove = (id) => setItems((cur) => cur.filter((it) => String(it.id) !== String(id)));
  const clear  = () => setItems([]);

  return (
    <WishlistContext.Provider value={{ items, has, toggle, remove, clear, totalCount: items.length }}>
      {children}
    </WishlistContext.Provider>
  );
}

export function useWishlist() {
  const ctx = useContext(WishlistContext);
  if (!ctx) throw new Error('useWishlist must be used inside <WishlistProvider>');
  return ctx;
}
